import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  Divider,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputBase,
  Link,
  Tooltip,
} from "@mui/material";
import {
  fetchQuestionById,
  fetchAnswers,
  likeAnswer,
  markBestAnswer,
  postAnswer,
  fetchAISettings,
  updateAISettings,
} from "../../store/queriesAnswerSlice";
import {
  fetchCommentsByAnswer,
  createComment,
  deleteComment,
  clearComments,
} from "../../store/commentsSlice";
import "./QueriesAnswer.css";
import "../HomeFolder/HomeContent.css";
import AskAI from "../HomeFolder/AskAI";
import { getPostedTime } from "../HomeFolder/QA";
import QARightSection from "../HomeFolder/QARightSection";
import { useParams } from "react-router-dom";
import { createTheme } from "@mui/material/styles";
import { ThemeProvider } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { sortAnswers } from "../HomeFolder/Utils/answerSortUtils";
const getUserIdFromToken = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.oid;
  } catch {
    return null;
  }
};

function QueriesAnswer() {
  const theme = createTheme({
    typography: {
      fontFamily: "Graphik-Regular",
    },
  });
  const [answer, setAnswer] = useState("");
  const [answerSortBy, setAnswerSortBy] = useState("");
  const [activeCommentAnswerId, setActiveCommentAnswerId] = useState(null);
  const [commentText, setCommentText] = useState("");
  const commentsByAnswer = useSelector((state) => state.comments.byAnswerId);

  const editorRef = useRef(null);
  const applyFormat = (type) => {
    if (!editorRef.current) return;
    editorRef.current.focus();

    switch (type) {
      case "bold":
        document.execCommand("bold");
        break;

      case "italic":
        document.execCommand("italic");
        break;

      case "underline":
        document.execCommand("underline");
        break;

      case "number":
        document.execCommand("insertOrderedList");
        break;

      case "bullet":
        document.execCommand("insertUnorderedList");
        break;

      case "small":
        document.execCommand("fontSize", false, "2");
        break;

      case "caps": {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const text = range.toString();
        if (!text) return;

        const span = document.createElement("span");
        span.style.textTransform = "uppercase";
        span.textContent = text;

        range.deleteContents();
        range.insertNode(span);
        selection.removeAllRanges();
        const newRange = document.createRange();
        newRange.setStartAfter(span);
        newRange.collapse(true);
        selection.addRange(newRange);
        break;
      }

      default:
        break;
    }
  };

  const { id: questionId } = useParams();
  const MAX_CHARS = 5000;
  const [charCount, setCharCount] = useState(0);
  const dispatch = useDispatch();
  const { question, answers, loading, aiEnabled, aiSettingsLoading } =
    useSelector((state) => state.queriesAnswer);

  const token = localStorage.getItem("token");

  const currentUserId = localStorage.getItem("userId") || getUserIdFromToken();
  const authHeader = {
    headers: { Authorization: token },
  };

  const handlePostAnswer = () => {
    const text = editorRef.current?.innerText?.trim();
    if (!text) return;

    if (text.length > MAX_CHARS) {
      alert(`Maximum ${MAX_CHARS} characters allowed`);
      return;
    }

    try {
      dispatch(postAnswer({ questionId, answer: text }));
      editorRef.current.innerHTML = "";
      setCharCount(0);
    } catch (error) {
      console.error("Post answer error:", error);
    }
  };

  useEffect(() => {
    dispatch(fetchQuestionById(questionId));
    dispatch(fetchAnswers(questionId));
    dispatch(fetchAISettings());
  }, [dispatch, questionId]);

  //if (!question) return null;
  const currentUserName = question?.username || "You";
  const currentUserDesignation = question?.designation || "";
  const isQuestionOwner = String(question?.user_id) === String(currentUserId);

  const bestAnswer = answers?.find((a) => a.is_best_answer);
  //const otherAnswers = answers?.filter((a) => !a.is_best_answer);
  const sortedAnswers = sortAnswers(
    answers.filter(
      (a) => !a.is_best_answer && a.answer_type !== "ai"
    ),
    answerSortBy,
  );
  const isBestAnswerOwner =
    bestAnswer && String(bestAnswer.user_id) === String(currentUserId);
  const canLikeBest =
    bestAnswer && !isBestAnswerOwner && !bestAnswer.liked_by_me;

  const handleEditorInput = () => {
    const text = editorRef.current?.innerText || "";

    if (text.length > MAX_CHARS) {
      editorRef.current.innerText = text.slice(0, MAX_CHARS);
      setCharCount(MAX_CHARS);
      return;
    }

    setCharCount(text.length);
  };

  const handlePostComment = (answerId) => {
    if (!commentText.trim()) return;

    dispatch(
      createComment({
        answerId,
        comment: commentText,
      }),
    ).then(() => {
      dispatch(fetchCommentsByAnswer(answerId));
    });

    setCommentText("");
  };

  const handleToggleComments = (answerId) => {
    const isOpen = activeCommentAnswerId === answerId;

    if (isOpen) {
      setActiveCommentAnswerId(null);
      dispatch(clearComments(answerId));
    } else {
      setActiveCommentAnswerId(answerId);
      dispatch(fetchCommentsByAnswer(answerId));
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="qaPageWrapper" sx={{ position: "relative" }}>
        {/* Breadcrumb */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "74%",
          }}
        >
          <Typography className="qaBreadcrumb">
            <Link
              component={RouterLink}
              to="/"
              underline="hover"
              color="inherit"
              sx={{ cursor: "pointer" }}
            >
              Home
            </Link>
            {" / "}
            <span>Queries and Answers</span>
          </Typography>

          {/* AI Toggle Group */}
          {isQuestionOwner && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Graphik-Regular",
                    fontSize: "14px",
                    fontWeight: 700,
                    lineHeight: "20px",
                    color: "#000",
                    whiteSpace: "nowrap",
                  }}
                >
                  {aiEnabled ? "Disable AI" : "Enable AI"}
                </Typography>

                <Box
                  component="button"
                  type="button"
                  aria-label={aiEnabled ? "Disable AI" : "Enable AI"}
                  aria-pressed={aiEnabled}
                  onClick={() => {
                    if (aiSettingsLoading) return;
                    dispatch(updateAISettings(!aiEnabled));
                  }}
                  sx={{
                    position: "relative",
                    width: "35px",
                    height: "18px",
                    minWidth: "35px",
                    padding: 0,
                    border: 0,
                    borderRadius: "10px",
                    backgroundColor: aiEnabled ? "#8A00FF" : "#9D9D9B",
                    cursor: "pointer",
                    flexShrink: 0,
                    transition: "background-color 0.15s ease",

                    "&:focus-visible": {
                      outline: "2px solid #7500C0",
                      outlineOffset: "2px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: "3px",
                      left: aiEnabled ? "21px" : "2px",
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      backgroundColor: "#fff",
                      transition: "left 0.15s ease",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                    }}
                  />
                </Box>
              </div>
              <Typography sx={{ fontStyle: "italic", fontSize: "9px" }}>
                <img
                  src={process.env.PUBLIC_URL + "/images/AskAI/note_icon.png"}
                  style={{ position: "relative", top: "2px", width: "10px" }}
                />
                Enable AI to generate answers
              </Typography>
            </div>
          )}
        </div>

        <Grid container spacing={3} className="qaMainLayout">
          {/* LEFT SECTION */}
          <Grid item xs={12} md={8}>
            {loading ? (
              <Box sx={{ textAlign: "center", mt: 6 }}>
                <Typography variant="h6" sx={{ color: "#000" }}>
                  Loading question...
                </Typography>
              </Box>
            ) : !question ? (
              <Box sx={{ textAlign: "center", mt: 6 }}>
                <Typography variant="h6">Question not found</Typography>
              </Box>
            ) : (
              <>
                {/* Question Card */}
                <Box
                  className="qa_box"
                  sx={{
                    border: "1px solid #B980DF",
                    p: 2,
                    paddingLeft: 0,
                    mb: 2,
                    display: "flex",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      backgroundColor: "#7500C0",
                      height: 40,
                    }}
                  />

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}
                    >
                      <Avatar sx={{ width: 36, height: 36, mr: 1.5 }} />

                      <Box>
                        <Typography fontWeight={600} sx={{ lineHeight: 1.2 }}>
                          {question?.username}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="#818180"
                          sx={{ lineHeight: 1.2 }}
                        >
                          {question?.designation}
                        </Typography>
                      </Box>

                      <Typography
                        variant="caption"
                        color="#757575"
                        sx={{ ml: "auto" }}
                      >
                        Posted {getPostedTime(question?.created_at)}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        color: "#7500C0",
                        fontWeight: 600,
                        mb: 1,
                        fontSize: "14px",
                      }}
                    >
                      {question?.body}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      {question.tags && question.tags.length > 0 && (
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                          {question.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              className="chipsbox"
                            />
                          ))}
                        </Box>
                      )}
                      {/* <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Chip label={question.technology} size="small" className="chipsbox" />
                    <Chip label={question.skill} size="small" className="chipsbox" />
                  </Box> */}

                      <Box
                        sx={{ display: "flex", gap: 2, alignItems: "center" }}
                      >
                        <Typography
                          variant="body2"
                          color="#7500C0"
                          fontWeight={600}
                        >
                          {answers.length}
                          <span
                            style={{
                              color: "#9D9D9B",
                              fontWeight: "500",
                              paddingLeft: "5px",
                            }}
                          >
                            answers
                          </span>
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <img
                            src={
                              process.env.PUBLIC_URL +
                              "/images/Home/dot_img.png"
                            }
                            alt="views"
                          />
                        </Box>
                        <Tooltip
                          title="Views"
                          arrow
                          classes={{
                            tooltip: "custom-tooltip",
                            arrow: "custom-tooltip-arrow",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              cursor: "pointer",
                            }}
                          >
                            <Box
                              component="img"
                              src={
                                process.env.PUBLIC_URL + "/images/Home/view.png"
                              }
                              alt="views"
                              sx={{
                                width: 16,
                                height: 16,
                                transition: "0.2s ease",
                                "&:hover": {
                                  content: `url(${process.env.PUBLIC_URL + "/images/Home/Visible.png"})`,
                                },
                              }}
                            />
                            <Typography variant="body2" color="#9D9D9B">
                              {question?.views}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* BEST ANSWER */}
                {bestAnswer && (
                  <>
                    <Typography className="qaSectionTitle">
                      Best rated answer
                    </Typography>

                    <Box
                      className="qa_box"
                      sx={{
                        border: "1px solid #B980DF",
                        p: 2,
                        paddingLeft: 0,
                        mb: 2,
                        display: "flex",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          padding: "16px",
                          paddingTop: "0px",
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Avatar sx={{ width: 36, height: 36, mr: 1.5 }} />

                          <Box>
                            <Typography
                              fontWeight={600}
                              sx={{ lineHeight: 1.2 }}
                            >
                              {bestAnswer?.username}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="#818180"
                              sx={{ lineHeight: 1.2 }}
                            >
                              {bestAnswer?.designation}
                            </Typography>
                          </Box>

                          <Typography
                            variant="caption"
                            color="#757575"
                            sx={{ ml: "auto" }}
                          >
                            Posted {getPostedTime(bestAnswer?.created_at)}
                          </Typography>
                        </Box>

                        <Typography
                          sx={{
                            color: "#555",
                            mb: 1,
                            fontSize: "14px",
                          }}
                        >
                          {bestAnswer?.answer}
                        </Typography>
                        <Divider />
                        <Box className="answerFooter">
                          <Box className="answerActions">
                            <Box className="actionItem">
                              <input
                                type="checkbox"
                                checked={bestAnswer?.is_best_answer}
                                readOnly
                                className="circleCheckbox"
                              />
                              <span>Best Answer</span>
                            </Box>

                            <Box className="actionItem">
                              <img
                                src={
                                  process.env.PUBLIC_URL +
                                  "/images/Home/like.png"
                                }
                                alt="Like"
                                className="qaActionIcon"
                                style={{
                                  opacity: canLikeBest ? 1 : 0.5,
                                  cursor: canLikeBest
                                    ? "pointer"
                                    : "not-allowed",
                                }}
                                onClick={() => {
                                  if (canLikeBest) {
                                    dispatch(
                                      likeAnswer({ answer: bestAnswer }),
                                    );
                                  }
                                }}
                              />
                              <span>{bestAnswer?.likes_count}</span>
                            </Box>

                            <Box className="actionItem">
                              <span className="iconWrapper">
                                <img
                                  src={
                                    process.env.PUBLIC_URL +
                                    "/images/Home/comment.png"
                                  }
                                  alt="Comment"
                                  className="qaActionIcon commentIcon"
                                  onClick={() =>
                                    handleToggleComments(bestAnswer.id)
                                  }
                                />
                              </span>
                            </Box>
                          </Box>
                        </Box>
                        {activeCommentAnswerId === bestAnswer.id && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1.5,
                              mt: 2,
                              mb: 2,
                            }}
                          >
                            <Avatar sx={{ width: 35, height: 35 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {/* <Typography fontWeight={600} sx={{ lineHeight: 1.2 }}>
                            {currentUserName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="#818180"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            {currentUserDesignation}
                          </Typography> */}

                              <Box className="commentInputBox">
                                <InputBase
                                  placeholder="Write a comment"
                                  className="commentInput"
                                  fullWidth
                                  value={commentText}
                                  onChange={(e) =>
                                    setCommentText(e.target.value)
                                  }
                                />

                                <img
                                  src={
                                    process.env.PUBLIC_URL +
                                    "/images/Home/send.png"
                                  }
                                  alt="Send"
                                  className="commentSendIcon"
                                  onClick={() =>
                                    handlePostComment(bestAnswer.id)
                                  }
                                />
                              </Box>
                            </Box>
                          </Box>
                        )}

                        {commentsByAnswer[bestAnswer.id]?.items?.map(
                          (comment) => (
                            <Box
                              key={comment.commentid}
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.5,
                                mt: 2,
                                mb: 2,
                              }}
                            >
                              <Avatar sx={{ width: 36, height: 36 }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  fontWeight={600}
                                  sx={{ lineHeight: 1.2 }}
                                >
                                  {comment.username}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="#818180"
                                  sx={{ mb: 0.5, display: "block" }}
                                >
                                  {comment.designation}
                                </Typography>
                                <Typography variant="body2" color="#555">
                                  {comment.comment}
                                </Typography>
                                {/* {currentUserId === comment.user_id && (
                            <Button
                              size="small"
                              onClick={() =>
                                dispatch(
                                  deleteComment({
                                    answerId: a.id,
                                    commentId: comment.commentid,
                                  })
                                ).then(() =>
                                  dispatch(fetchCommentsByAnswer(a.id))
                                )
                              }
                            >
                              Delete
                            </Button>
                          )} */}
                                <Divider sx={{ mt: 1 }} />
                              </Box>
                            </Box>
                          ),
                        )}
                      </Box>
                    </Box>
                  </>
                )}
                {/* ALL ANSWERS */}

                <Box className="qaSectionHeader">
                  <Typography className="qaSectionTitle">
                    All answers
                  </Typography>

                  <Box className="qaSortBy">
                    <FormControl
                      size="small"
                      className="custom-select sort-select"
                    >
                      <Select
                        displayEmpty
                        value={answerSortBy}
                        onChange={(e) => setAnswerSortBy(e.target.value)}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              "& .MuiMenuItem-root": {
                                fontSize: "14px",
                              },
                            },
                          },
                        }}
                        sx={{
                          minWidth: 160,
                        }}
                      >
                        <MenuItem value="">Sort by</MenuItem>
                        <MenuItem value="recent">Recent</MenuItem>
                        <MenuItem value="popular">Most Liked</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                <AskAI
                  questionId={question.id}
                  aiEnabled={aiEnabled}
                  isQuestionOwner={isQuestionOwner}
                />
                <Box
                  className="qa_box"
                  sx={{
                    border: "1px solid #B980DF",
                    p: 2,
                    paddingLeft: 0,
                    mb: 2,
                    display: "flex",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      padding: "16px",
                      paddingTop: "0px",
                      width: "100%",
                      paddingBottom: "0px",
                    }}
                  >
                    {sortedAnswers.length === 0 ? (
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="body2" color="#777">
                          {answers.length === 0
                            ? "No answers yet."
                            : answerSortBy
                              ? "No answers found for selected filter."
                              : "No additional answers available."}
                        </Typography>
                      </Box>
                    ) : (
                      sortedAnswers.map((a) => {
                        const isAnswerOwner =
                          String(a.user_id) === String(currentUserId);
                        const canMarkBest =
                          isQuestionOwner &&
                          !isAnswerOwner &&
                          !a.is_best_answer;
                        const canLike = !isAnswerOwner && !a.liked_by_me;
                        return (
                          <React.Fragment key={a.answerid}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                mb: 1,
                              }}
                            >
                              <Avatar sx={{ width: 36, height: 36, mr: 1.5 }} />

                              <Box>
                                <Typography
                                  fontWeight={600}
                                  sx={{ lineHeight: 1.2 }}
                                >
                                  {a.username}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="#818180"
                                  sx={{ lineHeight: 1.2 }}
                                >
                                  {a.designation}
                                </Typography>
                              </Box>

                              <Typography
                                variant="caption"
                                color="#757575"
                                sx={{ ml: "auto" }}
                              >
                                Posted {getPostedTime(a?.created_at)}
                              </Typography>
                            </Box>

                            <Typography
                              sx={{
                                color: "#555",
                                mb: 1,
                                fontSize: "14px",
                              }}
                            >
                              {a.answer}
                            </Typography>
                            <Divider />
                            <Box className="answerFooter" sx={{ mb: 4 }}>
                              <Box className="answerActions">
                                <Box className="actionItem">
                                  {isQuestionOwner && canMarkBest && (
                                    <>
                                      <input
                                        type="checkbox"
                                        className="circleCheckbox"
                                        checked={a.is_best_answer}
                                        disabled={!canMarkBest}
                                        style={{
                                          cursor: canMarkBest
                                            ? "pointer"
                                            : "not-allowed",
                                        }}
                                        onChange={() => {
                                          if (canMarkBest) {
                                            dispatch(
                                              markBestAnswer({
                                                questionId,
                                                answerId: a.answerid,
                                              }),
                                            );
                                          }
                                        }}
                                      />
                                      <span>Mark as Best Answer</span>
                                    </>
                                  )}
                                </Box>

                                <Box className="actionItem">
                                  <img
                                    src={
                                      process.env.PUBLIC_URL +
                                      "/images/Home/like.png"
                                    }
                                    alt="Like"
                                    className="qaActionIcon"
                                    style={{
                                      opacity: canLike ? 1 : 0.5,
                                      cursor: canLike
                                        ? "pointer"
                                        : "not-allowed",
                                    }}
                                    onClick={() => {
                                      if (canLike) {
                                        dispatch(likeAnswer({ answer: a }));
                                      }
                                    }}
                                  />
                                  <span>{a.likes_count}</span>
                                </Box>

                                <Box className="actionItem">
                                  <span className="iconWrapper">
                                    <img
                                      src={
                                        process.env.PUBLIC_URL +
                                        "/images/Home/comment.png"
                                      }
                                      alt="Comment"
                                      className="qaActionIcon commentIcon"
                                      onClick={() => handleToggleComments(a.id)}
                                    />
                                  </span>
                                </Box>
                              </Box>
                            </Box>
                            {activeCommentAnswerId === a.id && (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1.5,
                                  mt: 2,
                                  mb: 2,
                                }}
                              >
                                <Avatar sx={{ width: 36, height: 36 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  {/* <Typography fontWeight={600} sx={{ lineHeight: 1.2 }}>
                              {currentUserName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="#757575"
                              sx={{ mb: 0.5, display: "block" }}
                            >
                              {currentUserDesignation}
                            </Typography> */}

                                  <Box className="commentInputBox">
                                    <InputBase
                                      placeholder="Write a comment"
                                      className="commentInput"
                                      fullWidth
                                      value={commentText}
                                      onChange={(e) =>
                                        setCommentText(e.target.value)
                                      }
                                    />

                                    <img
                                      src={
                                        process.env.PUBLIC_URL +
                                        "/images/Home/send.png"
                                      }
                                      alt="Send"
                                      className="commentSendIcon"
                                      onClick={() => handlePostComment(a.id)}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            )}

                            {commentsByAnswer[a.id]?.items?.map((comment) => (
                              <Box
                                key={comment.commentid}
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1.5,
                                  mt: 2,
                                  mb: 2,
                                }}
                              >
                                <Avatar sx={{ width: 36, height: 36 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    fontWeight={600}
                                    sx={{ lineHeight: 1.2 }}
                                  >
                                    {comment.username}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="#818180"
                                    sx={{ mb: 0.5, display: "block" }}
                                  >
                                    {comment.designation}
                                  </Typography>
                                  <Typography variant="body2" color="#555">
                                    {comment.comment}
                                  </Typography>
                                  {/* {currentUserId === comment.user_id && (
                              <Button
                                size="small"
                                onClick={() =>
                                  dispatch(
                                    deleteComment({
                                      answerId: a.id,
                                      commentId: comment.commentid,
                                    })
                                  ).then(() =>
                                    dispatch(fetchCommentsByAnswer(a.id))
                                  )
                                }
                              >
                                Delete
                              </Button>
                            )} */}
                                  <Divider sx={{ mt: 1 }} />
                                </Box>
                              </Box>
                            ))}
                          </React.Fragment>
                        );
                      })
                    )}
                  </Box>
                </Box>
                {/* WRITE ANSWER */}
                <Typography className="qaSectionTitle">Your answer</Typography>

                <Box className="yourAnswerOuter">
                  {/* DOTTED BOX */}
                  <Box className="yourAnswerBox">
                    {/* Text area */}
                    <Box className="answerEditorWrapper">
                      <div
                        ref={editorRef}
                        className="answerEditor"
                        contentEditable
                        data-placeholder="Type"
                        suppressContentEditableWarning
                        onInput={handleEditorInput}
                      />
                    </Box>
                    <Divider className="yourAnswerDivider" />
                    <Box className="yourAnswerToolbar">
                      <img
                        src={process.env.PUBLIC_URL + "/images/Home/aa.png"}
                        alt="Aa"
                        onClick={() => applyFormat("small")}
                      />

                      <img
                        src={process.env.PUBLIC_URL + "/images/Home/bold.png"}
                        alt="Bold"
                        onClick={() => applyFormat("bold")}
                      />

                      <img
                        src={process.env.PUBLIC_URL + "/images/Home/italic.png"}
                        alt="Italic"
                        onClick={() => applyFormat("italic")}
                      />

                      <img
                        src={
                          process.env.PUBLIC_URL + "/images/Home/underline.png"
                        }
                        alt="Underline"
                        onClick={() => applyFormat("underline")}
                      />

                      <img
                        src={
                          process.env.PUBLIC_URL +
                          "/images/Home/number-list.png"
                        }
                        alt="Numbered list"
                        onClick={() => applyFormat("number")}
                      />

                      <img
                        src={
                          process.env.PUBLIC_URL +
                          "/images/Home/bullet-list.png"
                        }
                        alt="Bullet list"
                        onClick={() => applyFormat("bullet")}
                      />

                      <label className="attachIcon">
                        <img
                          src={
                            process.env.PUBLIC_URL + "/images/Home/attach.png"
                          }
                          alt="Attach"
                        />
                        <input type="file" hidden />
                      </label>

                      <img
                        src={process.env.PUBLIC_URL + "/images/Home/Help.png"}
                        alt="Help"
                        className="helpImg"
                      />
                    </Box>
                  </Box>
                  <Typography className="charLimit">
                    {charCount} of 5000 max characters
                  </Typography>

                  <Box className="answerFooterButtons">
                    <Button
                      variant="contained"
                      className="postAnswerBtn"
                      onClick={handlePostAnswer}
                    >
                      Post your answer
                    </Button>
                    <Button
                      variant="outlined"
                      className="discardAnswerBtn"
                      onClick={() => {
                        editorRef.current.innerHTML = "";
                        setCharCount(0);
                      }}
                    >
                      Discard
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </Grid>
          {/* RIGHT SECTION */}
          <Grid item xs={12} md={4}>
            <QARightSection />
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
}

export default QueriesAnswer;
